import React, { useEffect, useMemo, useState } from "react";
import api, { formatIDR } from "../lib/api";
import Layout from "../components/Layout";
import { ArrowDownCircle, ArrowUpCircle, Pencil, Trash2, Search, PiggyBank, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const today = () => new Date().toISOString().slice(0, 10);
const emptyIn = () => ({ type: "in", source: "manual", date: today(), amount: 0, note: "", order_id: "", category: "" });
const emptyOut = () => ({ type: "out", date: today(), amount: 0, note: "", order_id: "", category: "" });

const PettyCash = () => {
  const [data, setData] = useState({ entries: [], balance: 0, total_in: 0, total_out: 0, min_balance: 0 });
  const [cats, setCats] = useState([]);
  const [orders, setOrders] = useState([]);
  const [cashOrders, setCashOrders] = useState([]);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(emptyIn());
  const [editingId, setEditingId] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const params = {};
    if (filterType) params.type = filterType;
    if (start) params.start = start;
    if (end) params.end = end;
    const { data: d } = await api.get("/petty-cash", { params });
    setData(d);
  };
  const loadRefs = async () => {
    const [c, o] = await Promise.all([api.get("/expenses/categories"), api.get("/orders")]);
    setCats(c.data.categories || []);
    setOrders(o.data || []);
  };
  const loadCashOrders = async (includeId = "") => {
    const { data: d } = await api.get("/petty-cash/cash-orders", { params: includeId ? { include_order_id: includeId } : {} });
    setCashOrders(d || []);
  };

  useEffect(() => { loadRefs(); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterType, start, end]);

  const filtered = useMemo(() => {
    if (!search) return data.entries;
    const s = search.toLowerCase();
    return data.entries.filter((e) => [e.category, e.note, e.order?.order_no, e.order?.customer_name].some((v) => (v || "").toLowerCase().includes(s)));
  }, [search, data.entries]);

  const isLow = data.min_balance > 0 && data.balance <= data.min_balance;

  const openIn = () => { setForm(emptyIn()); setEditingId(null); loadCashOrders(); setModal(true); };
  const openOut = () => { setForm(emptyOut()); setEditingId(null); setModal(true); };
  const openEdit = (e) => {
    const f = { type: e.type, date: e.date, amount: e.amount, note: e.note || "", order_id: e.order_id || "", category: e.category || "" };
    if (e.type === "in") { f.source = e.order_id ? "order" : "manual"; loadCashOrders(e.order_id || ""); }
    setForm(f); setEditingId(e.id); setModal(true);
  };

  const pickCashOrder = (id) => {
    const o = cashOrders.find((x) => x.id === id);
    setForm((f) => ({
      ...f, order_id: id,
      amount: o ? o.amount : f.amount,
      note: o && !f.note ? `Pembayaran tunai ${o.order_no} · ${o.customer_name}` : f.note,
    }));
  };

  const showAlert = (alert) => {
    if (!alert?.low) return;
    if (alert.sent) toast.warning("Saldo petty cash di bawah batas — info WA terkirim");
    else toast.warning(`Saldo petty cash di bawah batas — WA tidak terkirim: ${alert.error || "cek Pengaturan"}`);
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (form.type === "in" && form.source === "order" && !form.order_id) { toast.error("Pilih order"); return; }
    const payload = {
      type: form.type, date: form.date, amount: Number(form.amount), note: form.note,
      category: form.type === "out" ? form.category : "",
      order_id: form.type === "in" && form.source === "manual" ? "" : form.order_id,
    };
    setSaving(true);
    try {
      const { data: res } = editingId ? await api.put(`/petty-cash/${editingId}`, payload) : await api.post("/petty-cash", payload);
      toast.success(form.type === "in" ? "Dana masuk tersimpan" : "Dana keluar tersimpan (juga tercatat di Pengeluaran)");
      showAlert(res.alert);
      setModal(false); load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menyimpan");
    } finally { setSaving(false); }
  };

  const remove = async () => {
    try {
      const { data: res } = await api.delete(`/petty-cash/${confirmDel.id}`);
      toast.success("Data dihapus");
      showAlert(res.alert);
    } catch (err) { toast.error(err?.response?.data?.detail || "Gagal menghapus"); }
    setConfirmDel(null); load();
  };

  const orderLabel = (id) => {
    const o = orders.find((x) => x.id === id);
    return o ? `${o.order_no} · ${o.customer?.name || ""}` : "—";
  };
  const entryOrderLabel = (e) => (e.order ? `${e.order.order_no} · ${e.order.customer_name || ""}` : e.order_id ? orderLabel(e.order_id) : "—");

  return (
    <Layout>
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="label-cap mb-2">Kas Tunai Admin</div>
          <h1 className="font-heading text-4xl font-black tracking-tight">Petty Cash</h1>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <button onClick={openIn} data-testid="petty-add-in" className="bg-[#059669] hover:bg-[#047857] text-white text-xs font-bold uppercase tracking-wider px-4 py-2.5 flex items-center gap-2">
            <ArrowDownCircle size={14} /> Dana Masuk
          </button>
          <button onClick={openOut} data-testid="petty-add-out" className="bg-[#DC2626] hover:bg-[#B91C1C] text-white text-xs font-bold uppercase tracking-wider px-4 py-2.5 flex items-center gap-2">
            <ArrowUpCircle size={14} /> Dana Keluar
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <div className={`border p-4 ${isLow ? "bg-[#DC2626] border-[#DC2626] text-white" : "bg-[#111113] border-[#111113] text-white"}`}>
          <div className="label-cap text-white/70 flex items-center gap-1.5"><PiggyBank size={12} /> Saldo Petty Cash</div>
          <div className="font-mono font-black text-2xl mt-1" data-testid="petty-balance">{formatIDR(data.balance)}</div>
          <div className="text-[10px] text-white/70 mt-1 flex items-center gap-1">
            {isLow && <AlertTriangle size={11} />}
            {data.min_balance > 0 ? `Batas bawah ${formatIDR(data.min_balance)}${isLow ? " — saldo menipis!" : ""}` : "Batas bawah belum diatur (Pengaturan)"}
          </div>
        </div>
        <div className="bg-white border border-[#E4E4E7] p-4">
          <div className="label-cap text-[#059669]">Total Masuk</div>
          <div className="font-mono font-bold text-xl mt-1">{formatIDR(data.total_in)}</div>
        </div>
        <div className="bg-white border border-[#E4E4E7] p-4">
          <div className="label-cap text-[#DC2626]">Total Keluar</div>
          <div className="font-mono font-bold text-xl mt-1">{formatIDR(data.total_out)}</div>
        </div>
      </div>

      <div className="bg-white border border-[#E4E4E7] p-4 mb-4 flex items-end gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-1 min-w-[200px] px-2">
          <Search size={14} className="text-[#52525B]" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari catatan/kategori/order…" className="flex-1 outline-none text-sm bg-transparent" data-testid="petty-search" />
        </div>
        <div>
          <label className="label-cap block mb-1">Jenis</label>
          <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="px-2 py-1.5 border border-[#E4E4E7] text-xs bg-white" data-testid="petty-type-filter">
            <option value="">Semua</option>
            <option value="in">Dana Masuk</option>
            <option value="out">Dana Keluar</option>
          </select>
        </div>
        <div>
          <label className="label-cap block mb-1">Dari</label>
          <input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="px-2 py-1.5 border border-[#E4E4E7] text-xs font-mono bg-white" />
        </div>
        <div>
          <label className="label-cap block mb-1">Sampai</label>
          <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="px-2 py-1.5 border border-[#E4E4E7] text-xs font-mono bg-white" />
        </div>
      </div>

      <div className="bg-white border border-[#E4E4E7] overflow-x-auto">
        <table className="w-full min-w-[720px]">
          <thead>
            <tr className="border-b border-[#E4E4E7]">
              <th className="text-left label-cap px-4 py-3">Tanggal</th>
              <th className="text-left label-cap px-4 py-3">Jenis</th>
              <th className="text-left label-cap px-4 py-3">Kategori</th>
              <th className="text-left label-cap px-4 py-3">Catatan</th>
              <th className="text-left label-cap px-4 py-3">Order Terkait</th>
              <th className="text-right label-cap px-4 py-3">Jumlah</th>
              <th className="text-right label-cap px-4 py-3">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan="7" className="p-8 text-center text-sm text-[#52525B]">Belum ada data petty cash.</td></tr>}
            {filtered.map((e) => (
              <tr key={e.id} className="border-b border-[#E4E4E7] row-hover" data-testid={`petty-row-${e.id}`}>
                <td className="px-4 py-3 text-sm font-mono">{e.date}</td>
                <td className="px-4 py-3">
                  {e.type === "in"
                    ? <span className="text-[10px] font-bold uppercase tracking-wider bg-[#059669]/10 text-[#059669] border border-[#059669]/30 px-1.5 py-0.5">Masuk{e.order_id ? " · Order" : ""}</span>
                    : <span className="text-[10px] font-bold uppercase tracking-wider bg-[#DC2626]/10 text-[#DC2626] border border-[#DC2626]/30 px-1.5 py-0.5">Keluar</span>}
                </td>
                <td className="px-4 py-3 text-sm font-bold">{e.category || "—"}</td>
                <td className="px-4 py-3 text-sm text-[#52525B] max-w-xs truncate">{e.note}</td>
                <td className="px-4 py-3 text-xs font-mono">{entryOrderLabel(e)}</td>
                <td className={`px-4 py-3 text-sm font-mono font-bold text-right ${e.type === "in" ? "text-[#059669]" : "text-[#DC2626]"}`}>
                  {e.type === "in" ? "+" : "−"}{formatIDR(e.amount)}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex gap-1">
                    <button onClick={() => openEdit(e)} className="p-2 hover:bg-[#F4F4F5] border border-[#E4E4E7]" data-testid={`petty-edit-${e.id}`}><Pencil size={14} /></button>
                    <button onClick={() => setConfirmDel(e)} className="p-2 hover:bg-[#DC2626]/10 text-[#DC2626] border border-[#E4E4E7]" data-testid={`petty-delete-${e.id}`}><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={() => setModal(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white border border-[#E4E4E7] w-full max-w-xl max-h-[92vh] overflow-y-auto" data-testid="petty-form">
            <div className="px-6 py-4 border-b border-[#E4E4E7]">
              <div className="label-cap">{editingId ? "Ubah" : "Tambah"} {form.type === "in" ? "Dana Masuk" : "Dana Keluar"}</div>
            </div>
            <div className="p-6 grid grid-cols-2 gap-4">
              {form.type === "in" && (
                <F label="Sumber Dana" colSpan>
                  <div className="flex gap-2">
                    {[["manual", "Input Manual"], ["order", "Dari Order Tunai (Selesai)"]].map(([k, l]) => (
                      <button type="button" key={k} onClick={() => setForm({ ...form, source: k, order_id: k === "manual" ? "" : form.order_id })}
                        className={`flex-1 px-3 py-2 text-xs font-bold uppercase border ${form.source === k ? "bg-[#002FA7] text-white border-[#002FA7]" : "border-[#E4E4E7] bg-white"}`}
                        data-testid={`petty-source-${k}`}>{l}</button>
                    ))}
                  </div>
                </F>
              )}
              {form.type === "in" && form.source === "order" && (
                <F label="Order (No · Pelanggan)" required colSpan>
                  <select required value={form.order_id} onChange={(e) => pickCashOrder(e.target.value)} className={ic} data-testid="petty-cash-order-select">
                    <option value="">— Pilih order selesai yang dibayar tunai —</option>
                    {cashOrders.map((o) => (
                      <option key={o.id} value={o.id}>{o.order_no} · {o.customer_name} · {formatIDR(o.amount)}{o.payment_status === "DP" ? " (DP)" : ""}</option>
                    ))}
                  </select>
                  {cashOrders.length === 0 && <p className="text-[10px] text-[#52525B] mt-1">Tidak ada order Selesai + Tunai yang belum dicatat.</p>}
                </F>
              )}
              <F label="Tanggal" required>
                <input type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={ic + " font-mono"} data-testid="petty-date" />
              </F>
              <F label="Jumlah (IDR)" required>
                <input type="number" required min="1" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className={ic + " font-mono"} data-testid="petty-amount" />
              </F>
              {form.type === "out" && (
                <>
                  <F label="Kategori" required>
                    <select required value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={ic} data-testid="petty-category">
                      <option value="">— Pilih kategori —</option>
                      {cats.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </F>
                  <F label="Order Terkait (opsional)">
                    <select value={form.order_id} onChange={(e) => setForm({ ...form, order_id: e.target.value })} className={ic} data-testid="petty-order-select">
                      <option value="">— Tidak terkait order —</option>
                      {orders.map((o) => <option key={o.id} value={o.id}>{o.order_no} · {o.customer?.name}</option>)}
                    </select>
                  </F>
                </>
              )}
              <F label="Catatan" colSpan>
                <textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className={ic} data-testid="petty-note" />
              </F>
              {form.type === "out" && <p className="col-span-2 text-[10px] text-[#52525B]">Dana keluar otomatis tercatat juga di menu Pengeluaran.</p>}
            </div>
            <div className="px-6 py-4 border-t border-[#E4E4E7] flex justify-end gap-2">
              <button type="button" onClick={() => setModal(false)} className="px-4 py-2 text-xs font-bold uppercase border border-[#E4E4E7]">Batal</button>
              <button type="submit" disabled={saving} className="px-4 py-2 text-xs font-bold uppercase bg-[#002FA7] text-white hover:bg-[#00227A] disabled:opacity-50" data-testid="petty-submit">{saving ? "Menyimpan…" : "Simpan"}</button>
            </div>
          </form>
        </div>
      )}

      {confirmDel && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={() => setConfirmDel(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white border border-[#E4E4E7] max-w-sm w-full p-6">
            <h3 className="font-heading text-lg font-bold mb-2">Hapus {confirmDel.type === "in" ? "Dana Masuk" : "Dana Keluar"}?</h3>
            <p className="text-sm text-[#52525B] mb-2">{confirmDel.date} · {formatIDR(confirmDel.amount)}</p>
            {confirmDel.type === "out" && <p className="text-xs text-[#52525B] mb-6">Entri terkait di menu Pengeluaran juga ikut terhapus.</p>}
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setConfirmDel(null)} className="px-4 py-2 text-xs font-bold uppercase border border-[#E4E4E7]">Batal</button>
              <button onClick={remove} className="px-4 py-2 text-xs font-bold uppercase bg-[#DC2626] text-white" data-testid="petty-confirm-delete">Hapus</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

const ic = "w-full px-3 py-2 border border-[#E4E4E7] text-sm outline-none focus:border-[#002FA7] bg-white";
const F = ({ label, children, required, colSpan }) => (
  <div className={colSpan ? "col-span-2" : ""}>
    <label className="label-cap block mb-1.5">{label}{required && <span className="text-[#DC2626]">*</span>}</label>
    {children}
  </div>
);

export default PettyCash;
